import { Injectable, isDevMode } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { Observable, of, tap, map, catchError } from 'rxjs';
import { AuthService } from './services/auth.service';
import { PermissionType } from './constants/permission.constant';

@Injectable({ providedIn: 'root' })
export class PermissionGuard {
  constructor(private authService: AuthService, private router: Router) {}

  canActivate(next: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<boolean> {
    const required = next.data?.['permission'] as PermissionType | undefined;
    if (!required) return of(true);

    // authInformation is populated by HomeComponent.ngOnInit, but this guard fires
    // BEFORE that on direct navigation / hard refresh — hasPermission() would then
    // short-circuit to false. Fetch profile here when missing.
    if (this.authService.authInformation) {
      return of(this.decide(required));
    }

    return this.authService.getUserInformation().pipe(
      tap(profile => this.authService.setAuthInformation(profile)),
      map(() => this.decide(required)),
      catchError(() => {
        if (isDevMode()) {
          console.log('[DEV] [PermissionGuard] profile fetch failed, denying');
        }
        this.router.navigate(['/dashboard']);
        return of(false);
      }),
    );
  }

  private decide(required: PermissionType): boolean {
    const allowed = this.authService.hasPermission(required);
    if (allowed) return true;

    if (isDevMode()) {
      console.log('[DEV] [PermissionGuard] missing permission, redirecting to dashboard', required);
    }
    this.router.navigate(['/dashboard']);
    return false;
  }
}
